import { nanoid } from 'nanoid/non-secure'
import finalPrice from '../helpers/product/finalPrice'
import { GetUser } from '../helpers/TrackingHelper'

export const LS_KEY = 'wishlist-v1'
export const VISITOR_KEY = 'visitor-id'
export const EMAIL_KEY = 'visitor-em'

function hasStorage(type) {
  try {
    const storage = window[type]
    const key = '__storage_test__'
    storage.setItem(key, key)
    storage.removeItem(key)
    return true
  } catch {
    return false
  }
}

// Define a tiny API around memoryStore to match Storage interface
const memoryStore = {}
const memoryStoreAPI = {
  getItem(key) { return memoryStore[key] ?? null },
  setItem(key, v) { memoryStore[key] = '' + v },
  removeItem(key) { delete memoryStore[key] },
}

// optional migration hook
function migrate(payload) {
  // future: if (!payload.version) { ... }
  return payload
}

/** Pick the best available storage: local → session → memory */
function getStorage() {
  if (typeof window === 'undefined') return memoryStoreAPI
  if (hasStorage('localStorage')) return window.localStorage
  if (hasStorage('sessionStorage')) return window.sessionStorage
  return memoryStoreAPI
}

// always resolve storage at call-time (SSR safe)
function read(key)   { return getStorage().getItem(key) }
function write(key,v){ return getStorage().setItem(key, v) }
function drop(key)   { return getStorage().removeItem(key) }

/**
 * @typedef {Object} WishQuery
 * @property {string} id             // uuid
 * @property {number|null} [serverId] // DB id (server-side), null until created
 * @property {string} sku
 * @property {number|null} [objectId]
 * @property {string[]} features
 * @property {number|null} [minPrice]
 * @property {number|null} [maxPrice]
 * @property {boolean} inStock
 * @property {boolean} onHold
 * @property {number|null} [initialPrice]
 * @property {number} createdAt
 * @property {number} updatedAt
 */

export function clearLocalWishlist() {
  drop(EMAIL_KEY)
  drop(VISITOR_KEY)
  drop(LS_KEY)
}

/** Ensure we have a persistent visitor ID in storage */
export function ensureVisitorId() {
  let vid = read(VISITOR_KEY)
  if (!vid) {
    vid = nanoid()
    write(VISITOR_KEY, vid)
  }
  return vid
}

/**
 * @returns {{ visitorId: string, queries: WishQuery[] }}
 */
export function getLocalWishlist() {
  const visitorId = ensureVisitorId()
  const raw = read(LS_KEY)
  let data = null
  try {
    data = raw ? migrate(JSON.parse(raw)) : null
  } catch {
    // ignore parse errors 
  }
  const queries = Array.isArray(data?.queries) ? data.queries.filter(Boolean) : []
  return { visitorId, queries }
}

/** Persist the full wishlist payload */
export function saveLocalWishlist({ visitorId, queries }) {
  write(LS_KEY, JSON.stringify({ version: 1, visitorId, queries }))
}
export function saveWishlistEmail(email) {
  if (!email) { drop(EMAIL_KEY); return }
  write(EMAIL_KEY, email)
}

export function getWishlistEmail() {
  const email = read(EMAIL_KEY)
  if(email) return email
  const user = GetUser()
  if(user) return user.email
  return ''
}

/** Build a fresh WishQuery from partial input */
export function makeWishQuery({
  product,
  features = [],
  objectId = null,
  minPrice = null,
  maxPrice = null,
}) {
  const now = Date.now()
  const sku = product.sku
  const inStock = product.in_stock
  const onHold = product.on_hold
  const initialPrice = finalPrice(product)
  return {
    id: nanoid(),
    serverId: null,
    sku,
    objectId,
    features,
    minPrice,
    maxPrice,
    inStock,
    onHold,
    initialPrice,
    createdAt: now,
    updatedAt: now,
  }
}
